import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Height that makes an element fill the rest of the viewport (the Master Kala
 * thread scrolls inside itself, so it needs a real pixel height). Measured,
 * not guessed: the element's own top, minus the phone tab bar when it is
 * rendered (`[data-bottom-bar]`, hidden on desktop), keyboard-aware through
 * `visualViewport`.
 */
export function useFillHeight(ref: RefObject<HTMLElement | null>, gap = 0): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const viewport = window.visualViewport;
      const available = viewport?.height ?? window.innerHeight;
      const top = el.getBoundingClientRect().top + window.scrollY - (viewport?.offsetTop ?? 0);
      const bar = document.querySelector<HTMLElement>('[data-bottom-bar]');
      const barHeight = bar && bar.offsetParent !== null ? bar.offsetHeight : 0;
      setHeight(Math.max(260, Math.round(available - top - barHeight - gap)));
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [ref, gap]);

  return height;
}
